import { Button } from "@bcc/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@bcc/components/ui/card";
import { MoreHorizontal } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { setSalesTrendChartTestId, setSalesTrendChartTitleTestId } from "./sales-trend-chart.utils";

interface SalesTrendDataPoint {
  date: string;
  sales: number;
}

interface SalesTrendData {
  sevenDay: SalesTrendDataPoint[];
  thirtyDay: SalesTrendDataPoint[];
}

interface SalesTrendChartProps {
  readonly data: SalesTrendData;
  readonly testId?: string;
}

export function SalesTrendChart({ data, testId }: SalesTrendChartProps) {
  const [period, setPeriod] = useState<"7" | "30">("7");
  const { t } = useTranslation();
  const chartData = period === "7" ? data.sevenDay : data.thirtyDay;

  return (
    <Card testId={testId ?? setSalesTrendChartTestId("container")} className="border-[#E5E7EB]">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold text-[#111827]" data-testid={setSalesTrendChartTitleTestId()}>
              {t("dashboardComponents.salesTrendChart.title")}
            </CardTitle>
            <p className="text-sm text-[#6B7280] mt-1" data-testid={setSalesTrendChartTestId("description")}>
              {t("dashboardComponents.salesTrendChart.description")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-2">
              <Button
                variant={period === "7" ? "default" : "outline"}
                size="sm"
                onClick={() => setPeriod("7")}
                className={period === "7" ? "bg-[#16A34A] hover:bg-[#15803D]" : ""}
                data-testid={setSalesTrendChartTestId("period-7d")}
              >
                {t("dashboardComponents.salesTrendChart.7days")}
              </Button>
              <Button
                variant={period === "30" ? "default" : "outline"}
                size="sm"
                onClick={() => setPeriod("30")}
                data-testid={setSalesTrendChartTestId("period-30d")}
              >
                {t("dashboardComponents.salesTrendChart.30days")}
              </Button>
            </div>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#D1D5DB" />
              <XAxis dataKey="date" className="text-xs" tick={{ fill: "#6B7280", fontSize: 12 }} />
              <YAxis
                className="text-xs"
                tick={{ fill: "#6B7280", fontSize: 12 }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                }}
                formatter={(value) => [`$${Number(value ?? 0).toFixed(2)}`, "Sales"]}
              />
              <Line type="monotone" dataKey="sales" stroke="#16A34A" strokeWidth={2} dot={{ fill: "#16A34A", r: 4 }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
